(() => {
  const ELEMENT_NAME = 'plainjane-bulk-shipping-calculator';
  if (customElements.get(ELEMENT_NAME)) return;

  const STATE_NAMES = {
    AA: 'Armed Forces Americas', AE: 'Armed Forces Europe', AK: 'Alaska', AL: 'Alabama',
    AP: 'Armed Forces Pacific', AR: 'Arkansas', AS: 'American Samoa', AZ: 'Arizona',
    CA: 'California', CO: 'Colorado', CT: 'Connecticut', DC: 'District of Columbia',
    DE: 'Delaware', FL: 'Florida', FM: 'Federated States of Micronesia', GA: 'Georgia',
    GU: 'Guam', HI: 'Hawaii', IA: 'Iowa', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', MA: 'Massachusetts', MD: 'Maryland',
    ME: 'Maine', MH: 'Marshall Islands', MI: 'Michigan', MN: 'Minnesota', MO: 'Missouri',
    MP: 'Northern Mariana Islands', MS: 'Mississippi', MT: 'Montana', NC: 'North Carolina',
    ND: 'North Dakota', NE: 'Nebraska', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
    NV: 'Nevada', NY: 'New York', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
    PR: 'Puerto Rico', PW: 'Palau', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
    TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VA: 'Virginia', VI: 'U.S. Virgin Islands',
    VT: 'Vermont', WA: 'Washington', WI: 'Wisconsin', WV: 'West Virginia', WY: 'Wyoming'
  };

  const EXACT_ZIPS = {
    '00501': 'NY', '00544': 'NY', '05501': 'MA', '05544': 'MA', '06390': 'NY',
    '75502': 'AR', '96799': 'AS', '96910': 'GU', '96912': 'GU', '96913': 'GU',
    '96915': 'GU', '96916': 'GU', '96917': 'GU', '96919': 'GU', '96921': 'GU',
    '96923': 'GU', '96928': 'GU', '96929': 'GU', '96930': 'GU', '96931': 'GU',
    '96932': 'GU', '96940': 'PW', '96941': 'FM', '96942': 'FM', '96943': 'FM',
    '96944': 'FM', '96950': 'MP', '96951': 'MP', '96952': 'MP', '96960': 'MH',
    '96970': 'MH'
  };

  const ZIP3_RANGES = [
    [6, 7, 'PR'], [8, 8, 'VI'], [9, 9, 'PR'], [10, 27, 'MA'], [28, 29, 'RI'],
    [30, 38, 'NH'], [39, 49, 'ME'], [50, 54, 'VT'], [55, 55, 'MA'], [56, 59, 'VT'],
    [60, 69, 'CT'], [70, 89, 'NJ'], [90, 98, 'AE'], [100, 149, 'NY'], [150, 196, 'PA'],
    [197, 199, 'DE'], [200, 200, 'DC'], [201, 201, 'VA'], [202, 205, 'DC'], [206, 219, 'MD'],
    [220, 246, 'VA'], [247, 268, 'WV'], [270, 289, 'NC'], [290, 299, 'SC'], [300, 319, 'GA'],
    [320, 349, 'FL'], [340, 340, 'AA'], [350, 369, 'AL'], [370, 385, 'TN'], [386, 397, 'MS'],
    [398, 399, 'GA'], [400, 427, 'KY'], [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'],
    [500, 528, 'IA'], [530, 549, 'WI'], [550, 567, 'MN'], [569, 569, 'DC'], [570, 577, 'SD'],
    [580, 588, 'ND'], [590, 599, 'MT'], [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'],
    [680, 693, 'NE'], [700, 715, 'LA'], [716, 729, 'AR'], [730, 749, 'OK'], [750, 799, 'TX'],
    [800, 816, 'CO'], [820, 831, 'WY'], [832, 838, 'ID'], [840, 847, 'UT'], [850, 865, 'AZ'],
    [870, 884, 'NM'], [885, 885, 'TX'], [889, 898, 'NV'], [900, 961, 'CA'], [962, 966, 'AP'],
    [967, 968, 'HI'], [970, 979, 'OR'], [980, 994, 'WA'], [995, 999, 'AK']
  ];

  class PlainJaneBulkShippingCalculator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.form = this.querySelector('[data-part="form"]');
      this.zipInput = this.querySelector('[data-part="zip"]');
      this.poundsSelect = this.querySelector('[data-part="pounds"]');
      this.submitButton = this.querySelector('[data-part="submit"]');
      this.result = this.querySelector('[data-part="result"]');
      this.resultTitle = this.querySelector('[data-part="result-title"]');
      this.resultDetail = this.querySelector('[data-part="result-detail"]');
      this.resultNote = this.querySelector('[data-part="result-note"]');
      this.ratesList = this.querySelector('[data-part="rates"]');
      this.blockedStateCodes = new Set(
        (this.dataset.blockedStateCodes || '')
          .split(',')
          .map((code) => code.trim().toUpperCase())
          .filter(Boolean)
      );

      this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
      this.zipInput?.addEventListener('input', () => {
        this.zipInput.removeAttribute('aria-invalid');
      });
      this.poundsSelect?.addEventListener('change', () => {
        this.poundsSelect.removeAttribute('aria-invalid');
      });
    }

    async handleSubmit(event) {
      event.preventDefault();
      const zip = this.normalizeZip(this.zipInput?.value || '');
      if (!zip) {
        this.renderError('Enter a valid 5-digit US ZIP code.', this.zipInput);
        return;
      }

      const quantity = this.normalizePounds(this.poundsSelect?.value || '');
      if (!quantity) {
        const maxPounds = this.getMaxPounds();
        this.renderError(`Choose a shipment size from 1 to ${maxPounds} pounds.`, this.poundsSelect);
        return;
      }

      const state = this.lookupState(zip);
      if (!state) {
        this.renderError('We could not match that ZIP code to a US destination. Please check it and try again.', this.zipInput);
        return;
      }

      this.zipInput.value = zip;
      this.poundsSelect.value = String(quantity);
      if (this.blockedStateCodes.has(state.code)) {
        this.renderRestricted(state, zip);
        return;
      }

      const selection = this.getSelection(quantity);
      if (!selection) {
        this.renderError('Choose an available product option before checking shipping.');
        return;
      }

      this.setLoading(true);
      try {
        const rates = await this.fetchRates({ ...selection, zip, provinceCode: state.code });
        if (!rates.length) {
          this.renderError(`No shipping rate is currently available for ${state.name} ${zip}. Please verify at checkout or contact support.`);
          return;
        }
        this.renderRates(state, zip, selection.quantity, rates);
      } catch (error) {
        const timedOut = error instanceof DOMException && error.name === 'AbortError';
        const quantityUnavailable = error instanceof Error && error.name === 'QuantityUnavailableError';
        this.renderError(
          quantityUnavailable
            ? error.message
            : timedOut
            ? 'The carrier took too long to respond. Please try again in a moment.'
            : 'We could not load a shipping rate right now. Please try again or confirm the rate at checkout.',
          quantityUnavailable ? this.poundsSelect : null
        );
      } finally {
        this.setLoading(false);
      }
    }

    normalizeZip(value) {
      const match = value.trim().match(/^(\d{5})(?:-\d{4})?$/);
      return match ? match[1] : null;
    }

    getMaxPounds() {
      return Math.max(1, Number.parseInt(this.dataset.maxPounds, 10) || 100);
    }

    normalizePounds(value) {
      const normalized = String(value).trim();
      if (!/^\d+$/.test(normalized)) return null;
      const pounds = Number.parseInt(normalized, 10);
      return pounds >= 1 && pounds <= this.getMaxPounds() ? pounds : null;
    }

    lookupState(zip) {
      const exactCode = EXACT_ZIPS[zip];
      if (exactCode) return { code: exactCode, name: STATE_NAMES[exactCode] };

      if (zip.startsWith('969')) return null;
      const zip3 = Number(zip.slice(0, 3));
      const range = ZIP3_RANGES.find(([start, end]) => zip3 >= start && zip3 <= end);
      if (!range) return null;
      const code = range[2];
      return { code, name: STATE_NAMES[code] };
    }

    getSelection(quantity) {
      const productForm = document.getElementById(this.dataset.productFormId || '');
      const variantValue = productForm?.querySelector('[name="id"]')?.value || this.dataset.variantId;
      const variantId = String(variantValue || '').replace(/\D/g, '');
      return variantId ? { variantId, quantity } : null;
    }

    async fetchRates({ variantId, quantity, zip, provinceCode }) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 22000);
      const query = `
        mutation PlainJaneBulkShippingEstimate($input: CartInput!) {
          cartCreate(input: $input) {
            cart {
              id
              lines(first: 1) {
                nodes { quantity }
              }
              ... @defer {
                deliveryGroups(first: 10, withCarrierRates: true) {
                  nodes {
                    deliveryOptions {
                      title
                      description
                      code
                      deliveryMethodType
                      estimatedCost { amount currencyCode }
                    }
                  }
                }
              }
            }
            userErrors { field message code }
            warnings { code message }
          }
        }
      `;
      const variables = {
        input: {
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity }],
          buyerIdentity: { countryCode: 'US' },
          delivery: {
            addresses: [{
              address: { deliveryAddress: { countryCode: 'US', provinceCode, zip } },
              selected: true,
              oneTimeUse: true,
              validationStrategy: 'COUNTRY_CODE_ONLY'
            }]
          }
        }
      };

      try {
        const response = await fetch(`/api/${this.dataset.apiVersion || '2026-07'}/graphql.json`, {
          method: 'POST',
          headers: {
            Accept: 'multipart/mixed; deferSpec=20220824, application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query, variables }),
          signal: controller.signal
        });
        const body = await response.text();
        if (!response.ok) throw new Error(`Shipping request failed with ${response.status}`);

        const payloads = this.parseGraphqlPayloads(body, response.headers.get('content-type') || '');
        const graphErrors = payloads.flatMap((payload) => payload.errors || []);
        if (graphErrors.length) throw new Error(graphErrors.map((error) => error.message).join('; '));

        const initial = payloads.find((payload) => payload.data?.cartCreate)?.data.cartCreate;
        if (!initial?.cart || initial.userErrors?.length) {
          throw new Error(initial?.userErrors?.map((error) => error.message).join('; ') || 'Cart estimate failed');
        }

        const availableQuantity = initial.cart.lines?.nodes?.[0]?.quantity || 0;
        if (availableQuantity !== quantity) {
          const availabilityError = new Error(
            availableQuantity > 0
              ? `Only ${availableQuantity} ${availableQuantity === 1 ? 'pound is' : 'pounds are'} currently available for this estimate. Enter ${availableQuantity} or fewer.`
              : `A live rate is not available for ${quantity} ${quantity === 1 ? 'pound' : 'pounds'} of this product right now.`
          );
          availabilityError.name = 'QuantityUnavailableError';
          throw availabilityError;
        }

        const groups = payloads
          .flatMap((payload) => payload.incremental || [])
          .flatMap((part) => part.data?.deliveryGroups?.nodes || []);
        const rates = groups
          .flatMap((group) => group.deliveryOptions || [])
          .filter((option) => option.deliveryMethodType === 'SHIPPING' && option.estimatedCost?.currencyCode)
          .map((option) => {
            const rate = {
              name: option.title || option.code || 'Shipping',
              code: option.code || '',
              description: option.description || '',
              amount: Number(option.estimatedCost.amount),
              currency: option.estimatedCost.currencyCode
            };
            return { ...rate, transit: this.getTransitEstimate(rate) };
          })
          .filter((rate) => Number.isFinite(rate.amount));

        const uniqueRates = new Map();
        rates.forEach((rate) => uniqueRates.set(`${rate.name}|${rate.amount}|${rate.currency}`, rate));
        return [...uniqueRates.values()].sort((a, b) => a.amount - b.amount);
      } finally {
        window.clearTimeout(timeout);
      }
    }

    parseGraphqlPayloads(body, contentType) {
      if (!contentType.includes('multipart/mixed')) return [JSON.parse(body)];
      const boundary = contentType.match(/boundary="?([^";]+)"?/i)?.[1];
      if (!boundary) throw new Error('Shipping response boundary was missing');

      return body
        .split(`--${boundary}`)
        .map((part) => {
          const start = part.indexOf('{');
          const end = part.lastIndexOf('}');
          if (start < 0 || end < start) return null;
          try {
            return JSON.parse(part.slice(start, end + 1));
          } catch (_error) {
            return null;
          }
        })
        .filter(Boolean);
    }

    getTransitEstimate(rate) {
      const service = `${rate.name} ${rate.code} ${rate.description}`.toLowerCase();
      if (service.includes('priority mail express')) return 'Est. 1–3 days after shipment';
      if (service.includes('priority mail')) return 'Est. 2–3 days after shipment';
      if (service.includes('ground advantage')) return 'Est. 2–5 days after shipment';
      return 'Delivery timing confirmed at checkout';
    }

    renderRestricted(state, zip) {
      this.poundsSelect?.removeAttribute('aria-invalid');
      this.zipInput.setAttribute('aria-invalid', 'true');
      this.showResult('is-restricted');
      this.resultTitle.textContent = `THCA delivery isn’t available to ${state.name}`;
      this.resultDetail.textContent = `ZIP ${zip} is in a destination where Plain Jane currently does not ship Bulk THCA products.`;
      this.ratesList.replaceChildren();
      this.resultNote.hidden = false;
    }

    renderRates(state, zip, quantity, rates) {
      this.zipInput.removeAttribute('aria-invalid');
      this.poundsSelect?.removeAttribute('aria-invalid');
      this.showResult('is-success');
      this.resultTitle.textContent = `Shipping ${quantity} ${quantity === 1 ? 'lb' : 'lbs'} to ${state.name} ${zip}`;
      this.resultDetail.textContent = `Current estimate for ${quantity} × ${this.dataset.variantTitle || 'selected item'}.`;
      this.ratesList.replaceChildren(...rates.map((rate) => this.createRateRow(rate)));
      this.resultNote.hidden = false;
    }

    createRateRow(rate) {
      const item = document.createElement('li');
      item.className = 'pj-bulk-shipping-calculator__rate';
      const name = document.createElement('span');
      name.className = 'pj-bulk-shipping-calculator__rate-name';
      name.textContent = rate.name;
      const transit = document.createElement('span');
      transit.className = 'pj-bulk-shipping-calculator__rate-time';
      transit.textContent = rate.transit;
      const service = document.createElement('span');
      service.className = 'pj-bulk-shipping-calculator__rate-service';
      service.append(name, transit);
      const price = document.createElement('strong');
      price.className = 'pj-bulk-shipping-calculator__rate-price';
      price.textContent = new Intl.NumberFormat(this.dataset.locale || 'en-US', {
        style: 'currency',
        currency: rate.currency
      }).format(rate.amount);
      item.append(service, price);
      return item;
    }

    renderError(message, invalidField = null) {
      this.zipInput?.removeAttribute('aria-invalid');
      this.poundsSelect?.removeAttribute('aria-invalid');
      invalidField?.setAttribute('aria-invalid', 'true');
      this.showResult('is-error');
      this.resultTitle.textContent = 'We couldn’t complete that estimate';
      this.resultDetail.textContent = message;
      this.ratesList.replaceChildren();
      this.resultNote.hidden = true;
    }

    showResult(stateClass) {
      this.result.classList.remove('is-success', 'is-restricted', 'is-error');
      this.result.classList.add(stateClass);
      this.result.hidden = false;
    }

    setLoading(loading) {
      this.classList.toggle('is-loading', loading);
      this.toggleAttribute('aria-busy', loading);
      if (this.submitButton) this.submitButton.disabled = loading;
    }
  }

  customElements.define(ELEMENT_NAME, PlainJaneBulkShippingCalculator);
})();
